import { Category, Question } from '@/types/assessment';

type StoredAnswerDetail = {
  id?: string;
  text?: string;
  value?: number;
} | null | undefined;

type StoredQuestionDetail = {
  questionId?: string;
  questionText?: string;
  current?: StoredAnswerDetail;
  target?: StoredAnswerDetail;
} | null | undefined;

type PrefilledResponse = {
  questionId: string;
  current: string[] | number;
  target: string[] | number;
};

const normalizeText = (value?: string) =>
  value?.trim().replace(/\s+/g, ' ').toLowerCase() ?? '';

const buildQuestionMaps = (categories: Category[]) => {
  const questionsById = new Map<string, Question>();
  const questionsByText = new Map<string, Question>();

  categories.forEach((category) => {
    category.questions.forEach((question) => {
      questionsById.set(question.id, question);
      questionsByText.set(normalizeText(question.text), question);
    });
  });

  return { questionsById, questionsByText };
};

const resolveChoiceAnswerId = (question: Question, answerDetail?: StoredAnswerDetail) => {
  if (!answerDetail) return undefined;

  if (answerDetail.id && question.answers.some((answer) => answer.id === answerDetail.id)) {
    return answerDetail.id;
  }

  const normalizedAnswerText = normalizeText(answerDetail.text);
  if (normalizedAnswerText) {
    const textMatch = question.answers.find(
      (answer) => normalizeText(answer.text) === normalizedAnswerText
    );

    if (textMatch) {
      return textMatch.id;
    }
  }

  if (typeof answerDetail.value === 'number') {
    const valueMatch = question.answers.find((answer) => answer.value === answerDetail.value);
    if (valueMatch) {
      return valueMatch.id;
    }
  }

  return undefined;
};

const resolveStoredValue = (question: Question, answerDetail?: StoredAnswerDetail) => {
  if (!answerDetail) return undefined;

  if (question.answerType === 'slider') {
    return typeof answerDetail.value === 'number' ? answerDetail.value : undefined;
  }

  const answerId = resolveChoiceAnswerId(question, answerDetail);
  return answerId ? [answerId] : [];
};

export const buildPrefilledResponsesFromAnswerDetails = (
  answerDetails: Record<string, StoredQuestionDetail> | undefined,
  categories: Category[]
): Record<string, PrefilledResponse> => {
  if (!answerDetails) return {};

  const { questionsById, questionsByText } = buildQuestionMaps(categories);
  const prefilled: Record<string, PrefilledResponse> = {};

  Object.values(answerDetails).forEach((detail) => {
    if (!detail) return;

    const matchedQuestion =
      (detail.questionId ? questionsById.get(detail.questionId) : undefined) ||
      (detail.questionText ? questionsByText.get(normalizeText(detail.questionText)) : undefined);

    if (!matchedQuestion) return;

    const currentValue = resolveStoredValue(matchedQuestion, detail.current);
    const targetValue = resolveStoredValue(matchedQuestion, detail.target);

    if (matchedQuestion.answerType === 'slider') {
      if (typeof currentValue !== 'number' || typeof targetValue !== 'number') return;

      prefilled[matchedQuestion.id] = {
        questionId: matchedQuestion.id,
        current: currentValue,
        target: targetValue,
      };

      return;
    }

    const normalizedCurrent = Array.isArray(currentValue) ? currentValue : [];
    const normalizedTarget = Array.isArray(targetValue) ? targetValue : [];

    if (normalizedCurrent.length === 0 && normalizedTarget.length === 0) return;

    prefilled[matchedQuestion.id] = {
      questionId: matchedQuestion.id,
      current: normalizedCurrent,
      target: normalizedTarget,
    };
  });

  return prefilled;
};